import type { BindableCommand } from './types';
import { enableCommandPalette } from 'src/lib/featureFlags/flags/enableCommandPalette';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Static registry of commands that both the keyboard-shortcut dispatcher and the keybindings
 * settings modal consume. The command palette produces its entries dynamically from widget/
 * workspace context, but the palette-facing commands here use the same `CommandAction` shape
 * and the same `WorkspaceService.executeCommand` dispatch path — demonstrating that the two
 * surfaces share one command abstraction.
 */
export function buildStaticCommands(featureFlagService: ReactBindings['FeatureFlagService']): BindableCommand[] {
    return [
        {
            id: 'commandPalette.toggle',
            label: 'Toggle Command Palette',
            category: 'Navigation',
            action: { eventKey: 'commandPalette::toggle', payload: {} },
            defaultBinding: { key: 'k', ctrl: true, shift: false, alt: false, meta: false },
            enabled: featureFlagService.getSignal(enableCommandPalette),
        },
        {
            id: 'workspace.cycleFocusForward',
            label: 'Cycle Widget Focus (Forward)',
            category: 'Navigation',
            action: { eventKey: 'workspace::cycleFocus', payload: { reverse: false } },
            defaultBinding: { code: 'Backquote', ctrl: true, shift: false, alt: false, meta: false },
        },
        {
            id: 'workspace.cycleFocusBackward',
            label: 'Cycle Widget Focus (Backward)',
            category: 'Navigation',
            action: { eventKey: 'workspace::cycleFocus', payload: { reverse: true } },
            defaultBinding: { code: 'Backquote', ctrl: true, shift: true, alt: false, meta: false },
        },
        {
            id: 'workspace.clearActiveContainer',
            label: 'Clear Focused Widget',
            category: 'Navigation',
            action: { eventKey: 'workspace::clearActiveContainer', payload: {} },
            defaultBinding: { code: 'Escape', ctrl: false, shift: false, alt: false, meta: false },
        },
        {
            id: 'keybindings.openSettings',
            label: 'Open Keyboard Shortcuts…',
            category: 'Settings',
            action: { eventKey: 'keybindings::openSettings', payload: {} },
            // Ctrl+Alt+K: chosen over Ctrl+, to avoid physical-key collisions with browser-reserved
            // shortcuts on non-QWERTY layouts (e.g. on Dvorak, Ctrl+, is physically Ctrl+W → close
            // tab, which browsers refuse to let preventDefault suppress).
            defaultBinding: { key: 'k', ctrl: true, shift: false, alt: true, meta: false },
        },
        // Palette-only examples — no default keybinding but still appear in the settings modal,
        // proving the registry is shared with the palette's action vocabulary.
        {
            id: 'workspace.addWidget.watchlist',
            label: 'Add Watchlist Widget',
            category: 'Widgets',
            action: { eventKey: 'workspace::addWidget', payload: { widgetType: 'watchlist' } },
            defaultBinding: null,
        },
        {
            id: 'workspace.addWidget.chart',
            label: 'Add Chart Widget',
            category: 'Widgets',
            action: { eventKey: 'workspace::addWidget', payload: { widgetType: 'tradingview-chart' } },
            defaultBinding: null,
        },
    ];
}
